import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App } from 'antd';

import { getFavorites, pinTargetOf, setCollectionFavorite, setCardFavorite, setFavoriteOrder, setPin, PIN_KIND, RECORD_LIST_COUNT, TILE_SOURCE } from '../services/favorites';
import { clearMyOverview, setMyOverview } from '../services/overview';
import { normalizeId } from '../utils/ids';
import { isTokenValid } from '../utils';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * App-wide cache of the current user's favorites AND Home pins. Both are
 * per-user rows (never stored on the shared collection/card — the
 * collection tier lives on the collection's own documents), and card
 * favorites are scoped per collection: the key is "<collectionId>:<cardId>".
 *
 * Toggles save immediately with an optimistic update and rollback; the API
 * calls are idempotent, so a retried toggle can't double-apply. A pin change
 * bumps `pinsVersion`, which is how the Home page knows to re-read itself.
 * A STAR does not: Home's Collections strip is derived from the favorites
 * here (useHome), so a star or unstar anywhere — Add to Home, the collection
 * bar, the collections list — moves the strip, the rail and the collections
 * page together with no second Home read. For that a star made here keeps the
 * identity of the collection it was made from (`favoriteCollectionRecords`),
 * so a strip that never saw the collection can still draw its badge.
 */
const FavoritesContext = createContext(null);

const cardKey = (collectionId, cardId) => `${normalizeId(collectionId)}:${normalizeId(cardId)}`;

// What Home's strip draws a collection with — the fields GET /api/home's
// `collectionIdentity` carries (keepr-api utils/homeUtils.js), no more.
const collectionIdentity = record => ({
	_id: normalizeId(record._id),
	name: record.name || '',
	icon: record.icon || null,
	color: record.color || null,
	kind: record.kind || null,
	status: record.status || null,
});

// A pin's identity on the client: an item, a tag or a combined list by its
// own id (an item names its collection; a tag is the pin wherever it lives,
// keepr-api favoriteUtils.identityOf; a combined list belongs to no
// collection), a launcher by (collection, target).
// The newest record of a card (KPR-1126) is (collection, card, saved filter
// or none): one card may be pinned unfiltered and under several filters. A
// collection shortcut (KPR-1125) is its collection's id alone. A list of a
// card's records (KPR-1128) is keyed as the newest-record pin is.
const ID_ONLY_PINS = new Set([PIN_KIND.ITEM, PIN_KIND.TAG, PIN_KIND.COMBINED_LIST, PIN_KIND.COLLECTION_LINK]);
export const pinKey = (kind, targetId, collectionId, savedFilterId = null) => {
	if (ID_ONLY_PINS.has(kind)) return `${kind}:${normalizeId(targetId)}`;
	if (kind === PIN_KIND.LATEST_RECORD || kind === PIN_KIND.RECORD_LIST) return `${kind}:${normalizeId(collectionId)}:${normalizeId(targetId)}:${normalizeId(savedFilterId) || '-'}`;
	return `${kind}:${normalizeId(collectionId)}:${normalizeId(targetId)}`;
};

/**
 * A record tile's identity on the client (KPR-1127): a fixed record by its
 * own id; the newest record of a card as the newest-record pin is keyed
 * (collection, card, filter or none). `target` is `{ source, targetId,
 * collectionId, savedFilterId }` — services/favorites pinTargetOf's shape.
 */
export const tileKey = (target) => (target?.source === TILE_SOURCE.LATEST
	? `${PIN_KIND.RECORD_TILE}:latest:${normalizeId(target.collectionId)}:${normalizeId(target.targetId)}:${normalizeId(target.savedFilterId) || '-'}`
	: `${PIN_KIND.RECORD_TILE}:record:${normalizeId(target?.targetId)}`);

const PIN_WORDS = {
	[PIN_KIND.ITEM]: 'record',
	[PIN_KIND.QUICK_ADD]: 'quick add',
	[PIN_KIND.MULTI_ADD]: 'multi add',
	[PIN_KIND.ADD_ITEM]: 'card',
	[PIN_KIND.TAG]: 'tag',
	[PIN_KIND.CHART_HOME]: 'chart',
	[PIN_KIND.DASHBOARD]: 'dashboard',
	[PIN_KIND.COMBINED_LIST]: 'list',
	[PIN_KIND.LATEST_RECORD]: 'shortcut',
	[PIN_KIND.SAVED_FILTER]: 'filter',
	[PIN_KIND.RECORD_TILE]: 'tile',
	[PIN_KIND.RECORD_LIST]: 'list of records',
	[PIN_KIND.COLLECTION_LINK]: 'collection',
};

const TAG_PIN_PREFIX = `${PIN_KIND.TAG}:`;

export function FavoritesProvider({ children }) {
	const [collectionIds, setCollectionIds] = useState(() => new Set());
	// The same ids in the caller's own order (the server's `position`) —
	// what the rail's Pinned section draws, and drags.
	const [collectionOrder, setCollectionOrder] = useState([]);
	const [cardKeys, setCardKeys] = useState(() => new Set());
	// pinKey → row id, for every Home pin the caller holds.
	const [pinRows, setPinRows] = useState(() => new Map());
	// tileKey → the element names a record tile shows (KPR-1127), so the
	// picker opens on the tile as it is.
	const [tileElements, setTileElements] = useState(() => new Map());
	// pinKey → how many records a list of records shows (KPR-1128), so its
	// count picker opens on the list as it is.
	const [listCounts, setListCounts] = useState(() => new Map());
	// collection id → its favorite ROW id (what an order write names).
	const [collectionRowIds, setCollectionRowIds] = useState(() => new Map());
	// collection id → identity, for a star made on this tab (see the header).
	const [collectionRecords, setCollectionRecords] = useState(() => new Map());
	// collection id → the dashboard this person chose for their own Overview
	// there (charts A7, a row of its own kind — one per collection). The raw
	// row, as GET /api/favorites keeps every row: whether it still stands is
	// the Overview read's answer (GET /api/collections/{id}/overview).
	const [overviewChoices, setOverviewChoices] = useState(() => new Map());
	// True once a load has ANSWERED — until then (or after a failed one, which
	// settles empty) the order says nothing about what is not a favorite, and
	// Home keeps its own read as it came.
	const [collectionsLoaded, setCollectionsLoaded] = useState(false);
	const [pinsVersion, setPinsVersion] = useState(0);
	// Moves when an Overview choice changes (charts A7): Overview re-reads.
	const [overviewVersion, setOverviewVersion] = useState(0);
	const [settled, setSettled] = useState(false);
	const [fetching, setFetching] = useState(false);
	const requestedRef = useRef(false);
	const location = useLocation();
	const { message } = App.useApp();

	// Another account (or none) on this tab: nothing of the last one's may
	// survive — its stars, its pin map (a "Pinned" on someone else's record),
	// its collection row ids (a drag that names them is a 400) — and the load
	// latch re-arms, or the next account would never load at all.
	const session = useOnSessionChange(() => {
		requestedRef.current = false;
		setCollectionIds(new Set());
		setCollectionOrder([]);
		setCardKeys(new Set());
		setPinRows(new Map());
		setTileElements(new Map());
		setListCounts(new Map());
		setCollectionRowIds(new Map());
		setCollectionRecords(new Map());
		setOverviewChoices(new Map());
		setCollectionsLoaded(false);
		setPinsVersion(version => version + 1);
		setFetching(false);
		setSettled(false);
	});

	/**
	 * Reads the favorites once per account (`force` re-reads). Resolves to the
	 * collection state it applied — `{ ids, order, rowIds }` — or null when it
	 * applied nothing (signed out, a failure, an answer for another account),
	 * so a caller that re-read to recover (reorderCollections) can act on the
	 * fresh rows without waiting for a render.
	 */
	const load = useCallback(async ({ force = false } = {}) => {
		if (requestedRef.current && !force) return null;
		if (!isTokenValid()) {
			// Signed out: nothing to ask (it would be a 401), and nothing
			// pending — settled, empty. The sign-in re-arms and loads.
			setSettled(true);
			return null;
		}
		requestedRef.current = true;
		const startedFor = getSessionUserId();
		// An answer for the account that was signed in when it was asked, only.
		const stale = () => getSessionUserId() !== startedFor;
		setFetching(true);
		try {
			const { collections, cards, pins } = await getFavorites();
			if (stale()) return null;
			const orderedCollections = collections.map(normalizeId).filter(Boolean);
			setCollectionIds(new Set(orderedCollections));
			setCollectionOrder(orderedCollections);
			setCardKeys(new Set(cards
				.filter(entry => entry?.collection_id && entry?.card_id)
				.map(entry => cardKey(entry.collection_id, entry.card_id))));
			const rows = new Map();
			const tiles = new Map();
			const counts = new Map();
			const collectionRows = new Map();
			const overview = new Map();
			pins.forEach(pin => {
				const rowId = normalizeId(pin?._id);
				if (!rowId) return;
				if (pin.targetType === 'collection') {
					collectionRows.set(normalizeId(pin.target_id), rowId);
				} else if (pin.targetType === 'overviewDashboard') {
					overview.set(normalizeId(pin.collection_id), normalizeId(pin.target_id));
				} else if (pin.targetType === PIN_KIND.LATEST_RECORD) {
					// Keyed by its card and filter: its target_id is either.
					rows.set(pinKey(pin.targetType, pin.card_id, pin.collection_id, pin.savedFilter_id), rowId);
				} else if (pin.targetType === PIN_KIND.RECORD_LIST) {
					const key = pinKey(pin.targetType, pin.card_id, pin.collection_id, pin.savedFilter_id);
					rows.set(key, rowId);
					counts.set(key, Number.isInteger(pin.count) ? pin.count : RECORD_LIST_COUNT.DEFAULT);
				} else if (pin.targetType === PIN_KIND.RECORD_TILE) {
					const key = tileKey(pinTargetOf(pin));
					rows.set(key, rowId);
					tiles.set(key, Array.isArray(pin.elements) ? pin.elements : []);
				} else if (Object.values(PIN_KIND).includes(pin.targetType)) {
					rows.set(pinKey(pin.targetType, pin.target_id, pin.collection_id), rowId);
				}
			});
			setPinRows(rows);
			setTileElements(tiles);
			setListCounts(counts);
			setCollectionRowIds(collectionRows);
			setOverviewChoices(overview);
			setCollectionsLoaded(true);
			return { ids: new Set(orderedCollections), order: orderedCollections, rowIds: collectionRows };
		} catch (err) {
			// Favorites are an enhancement; fail quiet and empty.
			return null;
		} finally {
			if (!stale()) {
				setFetching(false);
				setSettled(true);
			}
		}
	}, []);

	// Route changes and a change of account re-check; load() no-ops until
	// there is a valid token, then runs once per account.
	useEffect(() => {
		load();
	}, [load, location.pathname, session]);

	const isFavoriteCollection = useCallback(
		id => collectionIds.has(normalizeId(id)),
		[collectionIds]
	);
	const isFavoriteCard = useCallback(
		(collectionId, cardId) => cardKeys.has(cardKey(collectionId, cardId)),
		[cardKeys]
	);

	/**
	 * Star or unstar a collection. `record` is the collection the star is made
	 * from (any shape with its identity fields — a list row, the full
	 * document): pass it, and Home's strip draws the new favorite at once.
	 */
	const toggleCollectionFavorite = useCallback(async (id, record = null) => {
		const normalized = normalizeId(id);
		if (!normalized) return;
		const next = !collectionIds.has(normalized);
		const orderBefore = collectionOrder;
		if (next && record) {
			setCollectionRecords(current => new Map(current).set(normalized, collectionIdentity({ ...record, _id: normalized })));
		}
		setCollectionIds(current => {
			const updated = new Set(current);
			if (next) updated.add(normalized); else updated.delete(normalized);
			return updated;
		});
		// A new star lands at the END of the pinned order, as on the server.
		setCollectionOrder(current => (next
			? [...current.filter(entry => entry !== normalized), normalized]
			: current.filter(entry => entry !== normalized)));
		try {
			const response = await setCollectionFavorite(normalized, next);
			// The new row's id is what a later drag names (the API answers
			// with it; an older API does not, and the next load fills it in).
			const rowId = normalizeId(response?.data?._id);
			setCollectionRowIds(current => {
				const updated = new Map(current);
				if (next && rowId) updated.set(normalized, rowId);
				if (!next) updated.delete(normalized);
				return updated;
			});
		} catch (err) {
			setCollectionIds(current => {
				const updated = new Set(current);
				if (next) updated.delete(normalized); else updated.add(normalized);
				return updated;
			});
			setCollectionOrder(orderBefore);
			message.error('Could not update favorites. Please try again.');
		}
	}, [collectionIds, collectionOrder, message]);

	/**
	 * THE favorite collections' reorder — Home's Collections strip and the
	 * collections page's Favorites both save through here, and the rail
	 * draws the same `collectionOrder`, so one drag moves all three (keepr-api
	 * userFavorites `position`, section `collections`, is the one stored
	 * order). `orderedIds` are COLLECTION ids in their new order (the
	 * personal collection, which is never a favorite, is not among them).
	 * A PARTIAL list is fine, as on the server: favorites it does not name
	 * (one Home leaves out because it can no longer be opened) keep their
	 * relative order after it. Optimistic; a refusal puts the old order back
	 * and says so.
	 */
	const reorderCollections = useCallback(async (orderedIds) => {
		const plan = ({ ids, order, rowIds }) => {
			const listed = [...new Set((orderedIds || []).map(normalizeId).filter(id => id && ids.has(id)))];
			const named = new Set(listed);
			const next = [...listed, ...order.filter(id => !named.has(id))];
			return { next, rows: next.map(id => rowIds.get(id)).filter(Boolean) };
		};
		const first = plan({ ids: collectionIds, order: collectionOrder, rowIds: collectionRowIds });
		const before = collectionOrder;
		setCollectionOrder(first.next);
		try {
			await setFavoriteOrder('collections', first.rows);
			return;
		} catch (err) {
			// `unknown_pin`: this tab's favorites are older than the server's —
			// read once per session, they still name a favorite unstarred in
			// another tab since, and every drag from here would be refused the
			// same way until a reload. Re-read, then make the same move once
			// more against the fresh rows; if that is refused too, show the
			// server's order rather than one it will not keep.
			if (err?.response?.data?.code === 'unknown_pin') {
				const fresh = await load({ force: true });
				if (fresh) {
					const second = plan(fresh);
					setCollectionOrder(second.next);
					try {
						await setFavoriteOrder('collections', second.rows);
						return;
					} catch (retryErr) {
						setCollectionOrder(fresh.order);
						message.error('Could not save that order. Please try again.');
						return;
					}
				}
			}
			setCollectionOrder(before);
			message.error('Could not save that order. Please try again.');
		}
	}, [collectionIds, collectionOrder, collectionRowIds, load, message]);

	const isPinned = useCallback(
		(kind, targetId, collectionId, savedFilterId = null) => pinRows.has(pinKey(kind, targetId, collectionId, savedFilterId)),
		[pinRows]
	);

	// The tag ids this person has pinned (T5), in the server's order — how
	// the sidebar knows whether it has pinned tags to draw (and asks Home for
	// their names only then). Optimistic like the rows it is read from.
	const pinnedTagIds = useMemo(
		() => [...pinRows.keys()].filter(key => key.startsWith(TAG_PIN_PREFIX)).map(key => key.slice(TAG_PIN_PREFIX.length)),
		[pinRows]
	);

	/**
	 * Pin to / unpin from Home. `target` is `{ targetId, collectionId }` (a
	 * chart's may add `size`; a chart or dashboard is pinned in its CONTEXT,
	 * the collection whose items it counts; the newest record of a card
	 * names the card and may add `savedFilterId`).
	 * Optimistic; the API's own words come back on a refusal (the 50-a-kind
	 * cap says what to do next). Resolves to whether it is now pinned.
	 */
	const togglePin = useCallback(async (kind, target, { quiet = false } = {}) => {
		const key = pinKey(kind, target?.targetId, target?.collectionId, target?.savedFilterId);
		const next = !pinRows.has(key);
		const before = pinRows;
		setPinRows(current => {
			const updated = new Map(current);
			if (next) updated.set(key, current.get(key) || null); else updated.delete(key);
			return updated;
		});
		try {
			const result = await setPin(kind, target, next);
			if (next && result?._id) {
				setPinRows(current => new Map(current).set(key, normalizeId(result._id)));
			}
			setPinsVersion(version => version + 1);
			if (!quiet) {
				const word = PIN_WORDS[kind] || 'shortcut';
				message.success(next ? `Pinned the ${word} to Home.` : `Unpinned the ${word} from Home.`);
			}
			return next;
		} catch (err) {
			setPinRows(before);
			message.error(err?.response?.data?.message || 'Could not update your Home. Please try again.');
			return !next;
		}
	}, [pinRows, message]);

	/**
	 * The element names the person's tile of `target` shows (KPR-1127), or
	 * null when there is no such tile. `target`: `{ source, targetId,
	 * collectionId, savedFilterId }` (a fixed record: its id; the newest
	 * record: the card, its collection and filter).
	 */
	const tileFor = useCallback((target) => {
		const key = tileKey(target);
		return pinRows.has(key) ? (tileElements.get(key) || []) : null;
	}, [pinRows, tileElements]);

	/**
	 * Pin a record tile showing `elements` (one to three names), or change the
	 * names of the one already pinned (the API's PUT does both). Resolves to
	 * whether it saved; a refusal says the API's own words.
	 */
	const saveTile = useCallback(async (target, elements) => {
		const key = tileKey(target);
		const existed = pinRows.has(key);
		try {
			const result = await setPin(PIN_KIND.RECORD_TILE, { ...target, elements }, true);
			const rowId = normalizeId(result?._id) || pinRows.get(key) || null;
			setPinRows(current => new Map(current).set(key, rowId));
			setTileElements(current => new Map(current).set(key, Array.isArray(result?.elements) ? result.elements : elements));
			setPinsVersion(version => version + 1);
			message.success(existed ? 'Saved the tile.' : 'Pinned the tile to Home.');
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not save the tile. Please try again.');
			return false;
		}
	}, [pinRows, message]);

	/** Take a record tile off Home. Resolves to whether it is gone. */
	const removeTile = useCallback(async (target) => {
		const key = tileKey(target);
		try {
			await setPin(PIN_KIND.RECORD_TILE, target, false);
			setPinRows(current => { const next = new Map(current); next.delete(key); return next; });
			setTileElements(current => { const next = new Map(current); next.delete(key); return next; });
			setPinsVersion(version => version + 1);
			message.success('Unpinned the tile from Home.');
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not unpin the tile. Please try again.');
			return false;
		}
	}, [message]);

	/**
	 * How many records the person's list of `target` shows (KPR-1128), or
	 * null when there is no such list. `target`: `{ targetId (the card),
	 * collectionId, savedFilterId }`.
	 */
	const listFor = useCallback((target) => {
		const key = pinKey(PIN_KIND.RECORD_LIST, target?.targetId, target?.collectionId, target?.savedFilterId);
		return pinRows.has(key) ? (listCounts.get(key) || RECORD_LIST_COUNT.DEFAULT) : null;
	}, [pinRows, listCounts]);

	/**
	 * Pin a list of `target`'s records showing `count` of them, or change the
	 * count of the one already pinned (the API's PUT does both). Resolves to
	 * whether it saved; a refusal says the API's own words.
	 */
	const saveList = useCallback(async (target, count) => {
		const key = pinKey(PIN_KIND.RECORD_LIST, target?.targetId, target?.collectionId, target?.savedFilterId);
		const existed = pinRows.has(key);
		try {
			const result = await setPin(PIN_KIND.RECORD_LIST, { ...target, count }, true);
			const rowId = normalizeId(result?._id) || pinRows.get(key) || null;
			setPinRows(current => new Map(current).set(key, rowId));
			setListCounts(current => new Map(current).set(key, Number.isInteger(result?.count) ? result.count : count));
			setPinsVersion(version => version + 1);
			message.success(existed ? 'Saved the list.' : 'Pinned the list to Home.');
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not save the list. Please try again.');
			return false;
		}
	}, [pinRows, message]);

	/** Take a list of records off Home. Resolves to whether it is gone. */
	const removeList = useCallback(async (target) => {
		const key = pinKey(PIN_KIND.RECORD_LIST, target?.targetId, target?.collectionId, target?.savedFilterId);
		try {
			await setPin(PIN_KIND.RECORD_LIST, target, false);
			setPinRows(current => { const next = new Map(current); next.delete(key); return next; });
			setListCounts(current => { const next = new Map(current); next.delete(key); return next; });
			setPinsVersion(version => version + 1);
			message.success('Unpinned the list from Home.');
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not unpin the list. Please try again.');
			return false;
		}
	}, [message]);

	/** The dashboard this person chose for their own Overview of a collection (raw), or null. */
	const overviewChoiceFor = useCallback(
		collectionId => overviewChoices.get(normalizeId(collectionId)) || null,
		[overviewChoices]
	);

	/**
	 * "Show on my Overview" (a dashboard id) or "Use the collection's
	 * Overview" (null), for one collection (charts A7). Optimistic; the API's
	 * own words on a refusal. Resolves to whether it was saved; `overviewVersion`
	 * moves, which is how an Overview on screen knows to read itself again.
	 */
	const chooseMyOverview = useCallback(async (collectionId, dashboardId) => {
		const cid = normalizeId(collectionId);
		if (!cid) return false;
		const before = overviewChoices;
		setOverviewChoices(current => {
			const next = new Map(current);
			if (dashboardId) next.set(cid, normalizeId(dashboardId)); else next.delete(cid);
			return next;
		});
		try {
			if (dashboardId) await setMyOverview(cid, normalizeId(dashboardId));
			else await clearMyOverview(cid);
			setOverviewVersion(version => version + 1);
			message.success(dashboardId ? 'Your Overview shows this dashboard now.' : 'Your Overview shows the collection\'s dashboard again.');
			return true;
		} catch (err) {
			setOverviewChoices(before);
			message.error(err?.response?.data?.message || 'Could not change your Overview. Please try again.');
			return false;
		}
	}, [overviewChoices, message]);

	/** For surfaces that change what an Overview shows for everyone (a manager's choice). */
	const noteOverviewChanged = useCallback(() => {
		setOverviewVersion(version => version + 1);
	}, []);

	/** For surfaces that change pins themselves (the Home page's unpin and drag). */
	const notePinsChanged = useCallback(() => {
		setPinsVersion(version => version + 1);
		load({ force: true });
	}, [load]);

	/**
	 * For surfaces that change what Home shows WITHOUT a pin — a private tag
	 * put on or taken off a collection (tags b53: Home's `collectionGroups`).
	 * Every Home reader re-reads (the same `pinsVersion`); the favorites
	 * themselves did not change, so they are not asked again.
	 */
	const noteHomeChanged = useCallback(() => {
		setPinsVersion(version => version + 1);
	}, []);

	const toggleCardFavorite = useCallback(async (collectionId, cardId) => {
		const normalizedCollection = normalizeId(collectionId);
		const normalizedCard = normalizeId(cardId);
		if (!normalizedCollection || !normalizedCard) return;
		const key = cardKey(normalizedCollection, normalizedCard);
		const next = !cardKeys.has(key);
		setCardKeys(current => {
			const updated = new Set(current);
			if (next) updated.add(key); else updated.delete(key);
			return updated;
		});
		try {
			await setCardFavorite(normalizedCollection, normalizedCard, next);
		} catch (err) {
			setCardKeys(current => {
				const updated = new Set(current);
				if (next) updated.delete(key); else updated.add(key);
				return updated;
			});
			message.error('Could not update favorites. Please try again.');
		}
	}, [cardKeys, message]);

	const value = useMemo(() => ({
		isFavoriteCollection,
		isFavoriteCard,
		toggleCollectionFavorite,
		toggleCardFavorite,
		// The raw id set, for surfaces that ENUMERATE favorites (the rail's
		// pinned section) rather than test one id.
		favoriteCollectionIds: collectionIds,
		// …and the same ids in the caller's own order.
		favoriteCollectionOrder: collectionOrder,
		// …whether that order is the server's answer (see collectionsLoaded).
		favoriteCollectionsLoaded: collectionsLoaded,
		// Identity for collections starred on this tab (see the header).
		favoriteCollectionRecords: collectionRecords,
		reorderCollections,
		isPinned,
		togglePin,
		tileFor,
		saveTile,
		removeTile,
		listFor,
		saveList,
		removeList,
		pinnedTagIds,
		pinsVersion,
		notePinsChanged,
		noteHomeChanged,
		overviewChoiceFor,
		chooseMyOverview,
		overviewVersion,
		noteOverviewChanged,
		loading: fetching || !settled,
		ensureLoaded: load,
	}), [isFavoriteCollection, isFavoriteCard, toggleCollectionFavorite, toggleCardFavorite, collectionIds, collectionOrder, collectionsLoaded, collectionRecords, reorderCollections, isPinned, togglePin, tileFor, saveTile, removeTile, listFor, saveList, removeList, pinnedTagIds, pinsVersion, notePinsChanged, noteHomeChanged, overviewChoiceFor, chooseMyOverview, overviewVersion, noteOverviewChanged, fetching, settled, load]);

	return (
		<FavoritesContext.Provider value={value}>
			{children}
		</FavoritesContext.Provider>
	);
}

export function useFavorites() {
	const ctx = useContext(FavoritesContext);
	if (!ctx) {
		throw new Error('useFavorites must be used within a FavoritesProvider');
	}
	return ctx;
}

/** Null instead of throwing, for components rendered without the provider in tests. */
export function useOptionalFavorites() {
	return useContext(FavoritesContext);
}
